(() => {
  const materials = {
    ceramic: { front: [0.96, 0.95, 0.91], back: [0.93, 0.92, 0.88], edge: [0.86, 0.84, 0.79], shine: 0.20 },
    wood: { front: [0.82, 0.68, 0.48], back: [0.76, 0.61, 0.41], edge: [0.62, 0.47, 0.30], shine: 0.025 },
    mdf: { front: [0.84, 0.77, 0.65], back: [0.70, 0.58, 0.43], edge: [0.61, 0.49, 0.35], shine: 0.025 },
    acrylic: { front: [0.93, 0.96, 0.97], back: [0.82, 0.88, 0.91], edge: [0.73, 0.82, 0.86], shine: 0.24 },
    glass: { front: [0.92, 0.96, 0.97], back: [0.80, 0.89, 0.91], edge: [0.65, 0.79, 0.79], shine: 0.30 },
    aluminum: { front: [0.91, 0.92, 0.92], back: [0.69, 0.71, 0.72], edge: [0.57, 0.60, 0.62], shine: 0.25 },
    plastic: { front: [0.93, 0.95, 0.96], back: [0.85, 0.88, 0.90], edge: [0.77, 0.81, 0.83], shine: 0.18 }
  };
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  class VenroOrnamentTransparent3D extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.dataset.referenceOnly = 'true';
      this.dataset.modelFidelity = 'supplier-image-contour-optics-estimate';
      this.dataset.opticalFidelity = 'estimated';
      this.canvas = this.querySelector('canvas');
      this.abort = new AbortController();
      this.yaw = -0.22;
      this.pitch = 0.12;
      this.zoom = 0.78;
      this.pointers = new Map();
      if (!this.canvas) { this.fail('missing-canvas'); return; }
      const on = (element, type, handler, options = {}) => element?.addEventListener(type, handler, { ...options, signal: this.abort.signal });
      on(this.querySelector('[data-model-front]'), 'click', () => { this.yaw = 0; this.pitch = 0; this.draw(); });
      on(this.querySelector('[data-model-back]'), 'click', () => { this.yaw = Math.PI; this.pitch = 0; this.draw(); });
      on(this.querySelector('[data-model-side]'), 'click', () => { this.yaw = 1.38; this.pitch = 0.16; this.draw(); });
      on(this.querySelector('[data-model-reset]'), 'click', () => { this.yaw = -0.22; this.pitch = 0.12; this.zoom = 0.78; this.draw(); });
      on(this.querySelector('[data-model-in]'), 'click', () => this.changeZoom(1.18));
      on(this.querySelector('[data-model-out]'), 'click', () => this.changeZoom(1 / 1.18));
      on(this.canvas, 'keydown', event => {
        const turns = { ArrowLeft: [-0.15, 0], ArrowRight: [0.15, 0], ArrowUp: [0, -0.12], ArrowDown: [0, 0.12] };
        if (turns[event.key]) {
          event.preventDefault();
          this.yaw += turns[event.key][0];
          this.pitch = clamp(this.pitch + turns[event.key][1], -1.3, 1.3);
          this.draw();
        } else if (event.key === '+' || event.key === '=') { event.preventDefault(); this.changeZoom(1.1); }
        else if (event.key === '-') { event.preventDefault(); this.changeZoom(1 / 1.1); }
      });
      on(this.canvas, 'pointerdown', event => {
        this.canvas.focus({ preventScroll: true });
        this.canvas.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, [event.clientX, event.clientY]);
      });
      on(this.canvas, 'pointermove', event => {
        const previous = this.pointers.get(event.pointerId);
        if (!previous) return;
        const next = [event.clientX, event.clientY];
        if (this.pointers.size === 1) {
          this.yaw += (next[0] - previous[0]) * 0.012;
          this.pitch = clamp(this.pitch + (next[1] - previous[1]) * 0.01, -1.3, 1.3);
        } else {
          const other = [...this.pointers.entries()].find(([id]) => id !== event.pointerId)?.[1];
          if (other) {
            const oldDistance = Math.hypot(previous[0] - other[0], previous[1] - other[1]);
            const newDistance = Math.hypot(next[0] - other[0], next[1] - other[1]);
            if (oldDistance > 5) this.zoom = clamp(this.zoom * newDistance / oldDistance, 0.45, 2.4);
          }
        }
        this.pointers.set(event.pointerId, next);
        this.draw();
      });
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) on(this.canvas, type, event => this.pointers.delete(event.pointerId));
      on(this.canvas, 'wheel', event => {
        if (document.activeElement !== this.canvas) return;
        event.preventDefault();
        this.changeZoom(Math.exp(-event.deltaY * 0.001));
      }, { passive: false });
      on(this.canvas, 'webglcontextlost', event => { event.preventDefault(); this.contextLost = true; this.fail('webgl-context-lost'); });
      on(this.canvas, 'webglcontextrestored', () => {
        this.contextLost = false;
        this.gl = null;
        this.meshKey = null;
        this.uploadedSource = null;
        this.uploadedVersion = null;
      });
      this.resize = new ResizeObserver(() => this.draw());
      this.resize.observe(this.canvas);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      if (this.gl) {
        this.gl.deleteBuffer(this.buffer);
        this.gl.deleteTexture(this.texture);
        this.gl.deleteTexture(this.referenceTexture);
        this.gl.deleteProgram(this.program);
      }
      this.gl = null;
      this.buffer = null;
      this.texture = null;
      this.referenceTexture = null;
      this.referenceImage = null;
      this.maskImage = null;
      this.program = null;
      this.meshKey = null;
      this.uploadedSource = null;
      this.uploadedVersion = null;
      this.spec = null;
      this.failed = false;
      this.contextLost = false;
      this.bound = false;
      this.pointers?.clear();
    }

    changeZoom(factor) { this.zoom = clamp(this.zoom * factor, 0.45, 2.4); this.draw(); }

    fail(reason) {
      this.dataset.ready = 'false';
      this.dataset.error = reason;
      this.spec = null;
      if (this.gl) { this.gl.clearColor(0, 0, 0, 0); this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT); }
      const fallback = this.querySelector('[data-model-fallback]');
      if (fallback) fallback.hidden = false;
      this.dispatchEvent(new CustomEvent('model-unavailable', { bubbles: true, detail: { reason, referenceOnly: true } }));
    }

    show(source, specification, version) {
      const shape = String(specification?.shape || '').toLowerCase();
      const material = String(specification?.material || '').toLowerCase();
      const widthCm = Number(specification?.widthCm);
      const heightCm = Number(specification?.heightCm);
      const thicknessMm = Number(specification?.thicknessMm);
      const evidence = specification?.source || specification?.evidence;
      const referenceImage = specification?.referenceImage;
      const maskImage = specification?.maskImage;
      const optical = specification?.opticalAppearance;
      const opacities = [optical?.frontOpacity, optical?.backOpacity, optical?.edgeOpacity].map(Number);
      if (!source || !referenceImage || !maskImage || specification?.flatGeometryConfirmed !== true || specification?.silhouetteVerified !== true || specification?.referenceSurfaceBlank !== true || optical?.basis !== 'visual-estimate' || typeof optical?.description !== 'string' || !optical.description.trim() || opacities.some(v => !Number.isFinite(v) || v <= 0 || v >= 1) || !/^[a-z0-9_-]{1,40}$/.test(shape) || !['glass', 'acrylic'].includes(material) ||
          !Number.isFinite(widthCm) || !Number.isFinite(heightCm) || !Number.isFinite(thicknessMm) ||
          widthCm <= 0 || heightCm <= 0 || thicknessMm <= 0 || thicknessMm >= Math.min(widthCm, heightCm) * 10 ||
          typeof evidence !== 'string' || !evidence.trim()) {
        this.fail('unverified-dimensions-or-source');
        return false;
      }
      if ((material === 'glass' || material === 'acrylic') && !specification.maskImage) { this.fail('transparent-material-mask-required'); return false; }
      if (!this.bound || !this.canvas) { this.fail('not-connected'); return false; }
      if (this.contextLost) { this.fail('webgl-context-lost'); return false; }
      try {
        if (!this.gl) this.init();
        this.spec = { shape, material, widthCm, heightCm, thicknessMm, source: evidence, opticalAppearance: { frontOpacity: opacities[0], backOpacity: opacities[1], edgeOpacity: opacities[2], basis: optical.basis, description: optical.description } };
        const key = `${shape}:${material}:${widthCm}:${heightCm}:${thicknessMm}:${evidence}`;
        if (this.meshKey !== key || this.referenceImage !== referenceImage || this.maskImage !== maskImage) {
          const gl = this.gl;
          const geometry = this.referenceGeometry(referenceImage, maskImage, specification);
          const vertices = this.mesh(this.spec, geometry);
          gl.activeTexture(gl.TEXTURE1);
          gl.bindTexture(gl.TEXTURE_2D, this.referenceTexture);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, geometry.texture);
          gl.activeTexture(gl.TEXTURE0);
          this.referenceImage = referenceImage;
          this.maskImage = maskImage;
          this.dataset.holes = String(geometry.holes);
          this.dataset.maskResolution = String(geometry.size);
          gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
          gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
          this.count = vertices.length / 9;
          this.meshKey = key;
        }
        if (version == null || this.uploadedSource !== source || this.uploadedVersion !== String(version)) {
          const gl = this.gl;
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, this.texture);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
          this.uploadedSource = source;
          this.uploadedVersion = String(version);
        }
        Object.assign(this.dataset, { ready: 'true', shape, material, widthCm: String(widthCm), heightCm: String(heightCm), thicknessMm: String(thicknessMm), designVersion: String(version) });
        delete this.dataset.error;
        const fallback = this.querySelector('[data-model-fallback]');
        if (fallback) fallback.hidden = true;
        this.draw();
        return true;
      } catch (error) {
        this.fail(error?.message || 'webgl-or-texture-unavailable');
        return false;
      }
    }

    init() {
      const gl = this.canvas.getContext('webgl', { alpha: true, antialias: true, preserveDrawingBuffer: true });
      if (!gl) throw new Error('WebGL unavailable');
      this.gl = gl;
      const vertex = `precision mediump float;attribute vec3 aPosition;attribute vec3 aNormal;attribute vec2 aUV;attribute float aSurface;uniform vec2 uAngles;uniform float uDistance;uniform float uAspect;varying vec3 vNormal;varying vec3 vPosition;varying vec2 vUV;varying float vSurface;
      vec3 rotate(vec3 p){float c=cos(uAngles.x),s=sin(uAngles.x),cx=cos(uAngles.y),sx=sin(uAngles.y);vec3 q=vec3(c*p.x+s*p.z,p.y,-s*p.x+c*p.z);return vec3(q.x,cx*q.y-sx*q.z,sx*q.y+cx*q.z);}
      void main(){vec3 p=rotate(aPosition);vNormal=rotate(aNormal);vPosition=p;vUV=aUV;vSurface=aSurface;p.z-=uDistance;gl_Position=vec4(p.x*3.4/uAspect,p.y*3.4,-1.002002*p.z-.2002002,-p.z);}`;
      const fragment = `precision mediump float;varying vec3 vNormal;varying vec3 vPosition;varying vec2 vUV;varying float vSurface;uniform sampler2D uTexture;uniform sampler2D uReferenceTexture;uniform vec3 uFront;uniform vec3 uBack;uniform vec3 uEdge;uniform float uShine;uniform float uDistance;uniform vec3 uOpacity;
      void main(){vec3 n=normalize(vNormal),l=normalize(vec3(-.4,.65,1.5)),eye=normalize(vec3(0.,0.,uDistance)-vPosition);float light=.68+.32*max(dot(n,l),0.);float spec=pow(max(dot(n,normalize(l+eye)),0.),65.)*uShine;vec3 col;float opacity;if(vSurface<.5){vec4 ink=texture2D(uTexture,vUV);vec4 reference=texture2D(uReferenceTexture,vUV);vec3 base=mix(uFront,reference.rgb,reference.a);opacity=uOpacity.x+(1.-uOpacity.x)*ink.a;col=(base*uOpacity.x*(1.-ink.a)+ink.rgb*ink.a)/max(opacity,.00001);}else if(vSurface<1.5){col=uBack;opacity=uOpacity.y;}else{col=uEdge;opacity=uOpacity.z;}gl_FragColor=vec4(col*light+vec3(spec),opacity);}`;
      const shader = (type, text) => {
        const result = gl.createShader(type);
        gl.shaderSource(result, text);
        gl.compileShader(result);
        if (!gl.getShaderParameter(result, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(result));
        return result;
      };
      const vs = shader(gl.VERTEX_SHADER, vertex), fs = shader(gl.FRAGMENT_SHADER, fragment);
      this.program = gl.createProgram();
      gl.attachShader(this.program, vs);
      gl.attachShader(this.program, fs);
      gl.linkProgram(this.program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) throw new Error('Shader link');
      gl.useProgram(this.program);
      this.uniform = {};
      for (const name of ['uAngles', 'uDistance', 'uAspect', 'uTexture', 'uReferenceTexture', 'uFront', 'uBack', 'uEdge', 'uShine', 'uOpacity']) this.uniform[name] = gl.getUniformLocation(this.program, name);
      this.buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
      let offset = 0;
      for (const [name, size] of [['aPosition', 3], ['aNormal', 3], ['aUV', 2], ['aSurface', 1]]) {
        const location = gl.getAttribLocation(this.program, name);
        gl.enableVertexAttribArray(location);
        gl.vertexAttribPointer(location, size, gl.FLOAT, false, 36, offset * 4);
        offset += size;
      }
      this.texture = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.uniform1i(this.uniform.uTexture, 0);
      this.referenceTexture = gl.createTexture();
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this.referenceTexture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.uniform1i(this.uniform.uReferenceTexture, 1);
      gl.activeTexture(gl.TEXTURE0);
      gl.enable(gl.DEPTH_TEST);
      gl.enable(gl.CULL_FACE);
      gl.clearColor(0, 0, 0, 0);
    }

    referenceGeometry(image, maskImage, specification) {
      const imageWidth = image.naturalWidth || image.videoWidth || image.width;
      const imageHeight = image.naturalHeight || image.videoHeight || image.height;
      const maskWidth = maskImage.naturalWidth || maskImage.videoWidth || maskImage.width;
      const maskHeight = maskImage.naturalHeight || maskImage.videoHeight || maskImage.height;
      if (!(imageWidth > 0 && imageHeight > 0) || imageWidth !== maskWidth || imageHeight !== maskHeight) throw new Error('aligned-reference-and-mask-required');
      const scanSize = 1024, probe = document.createElement('canvas');
      probe.width = scanSize; probe.height = scanSize;
      const probeContext = probe.getContext('2d', { willReadFrequently: true });
      probeContext.drawImage(maskImage, 0, 0, scanSize, scanSize);
      const pixels = probeContext.getImageData(0, 0, scanSize, scanSize).data;
      let minX = scanSize, minY = scanSize, maxX = -1, maxY = -1, solid = 0, transparent = 0;
      for (let y = 0; y < scanSize; y++) for (let x = 0; x < scanSize; x++) {
        const alpha = pixels[(y * scanSize + x) * 4 + 3];
        if (alpha >= 160) { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); solid++; }
        if (alpha < 16) transparent++;
      }
      if (solid < scanSize * scanSize * 0.02 || transparent < scanSize * scanSize * 0.02 || maxX <= minX || maxY <= minY) throw new Error('transparent-silhouette-required');
      const texture = document.createElement('canvas');
      const size = 768;
      texture.width = size; texture.height = size;
      const ctx = texture.getContext('2d');
      ctx.drawImage(image, minX * imageWidth / scanSize, minY * imageHeight / scanSize, (maxX - minX + 1) * imageWidth / scanSize, (maxY - minY + 1) * imageHeight / scanSize, 0, 0, size, size);
      const sampledMask = document.createElement('canvas'); sampledMask.width = size; sampledMask.height = size;
      const maskContext = sampledMask.getContext('2d', { willReadFrequently: true });
      maskContext.drawImage(probe, minX, minY, maxX - minX + 1, maxY - minY + 1, 0, 0, size, size);
      const rgba = maskContext.getImageData(0, 0, size, size).data;
      const occupied = new Uint8Array(size * size);
      for (let i = 0; i < occupied.length; i++) occupied[i] = rgba[4 * i + 3] >= 160 ? 1 : 0;
      const visited = new Uint8Array(occupied.length), queue = new Int32Array(occupied.length);
      let largest = [], largestCount = 0;
      for (let start = 0; start < occupied.length; start++) {
        if (!occupied[start] || visited[start]) continue;
        let head = 0, tail = 0; queue[tail++] = start; visited[start] = 1;
        while (head < tail) {
          const index = queue[head++], x = index % size, y = Math.floor(index / size);
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
            const next = ny * size + nx;
            if (occupied[next] && !visited[next]) { visited[next] = 1; queue[tail++] = next; }
          }
        }
        if (tail > largestCount) { largestCount = tail; largest = Array.from(queue.subarray(0, tail)); }
      }
      if (largestCount < size * size * 0.02) throw new Error('silhouette-too-small');
      const occupiedCount = occupied.reduce((sum, cell) => sum + cell, 0);
      if (occupiedCount - largestCount > Math.max(8, largestCount * 0.001)) throw new Error('reference-has-disconnected-components');
      occupied.fill(0); for (const index of largest) occupied[index] = 1;
      const exterior = new Uint8Array(occupied.length); let head = 0, tail = 0;
      const enqueue = index => { if (!occupied[index] && !exterior[index]) { exterior[index] = 1; queue[tail++] = index; } };
      for (let i = 0; i < size; i++) { enqueue(i); enqueue((size - 1) * size + i); enqueue(i * size); enqueue(i * size + size - 1); }
      while (head < tail) {
        const index = queue[head++], x = index % size, y = Math.floor(index / size);
        if (x) enqueue(index - 1); if (x < size - 1) enqueue(index + 1);
        if (y) enqueue(index - size); if (y < size - 1) enqueue(index + size);
      }
      const holeVisited = new Uint8Array(occupied.length); let holes = 0;
      for (let start = 0; start < occupied.length; start++) {
        if (occupied[start] || exterior[start] || holeVisited[start]) continue;
        head = 0; tail = 0; queue[tail++] = start; holeVisited[start] = 1;
        while (head < tail) {
          const index = queue[head++], x = index % size, y = Math.floor(index / size);
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
            const next = ny * size + nx;
            if (!occupied[next] && !exterior[next] && !holeVisited[next]) { holeVisited[next] = 1; queue[tail++] = next; }
          }
        }
        if (tail >= 4) holes++;
        else for (let i = 0; i < tail; i++) occupied[queue[i]] = 1;
      }
      if (!holes && specification.allowNoVisibleHole !== true) throw new Error('reference-hole-not-visible');
      return { occupied, size, texture, holes, crop: { minX, minY, maxX, maxY } };
    }

    mesh(specification, geometry) {
      const { size, occupied } = geometry;
      const ratio = specification.heightCm / specification.widthCm;
      const halfDepth = specification.thicknessMm / 10 / specification.widthCm;
      const vertices = [];
      const triangle = (a, b, c) => vertices.push(...a, ...b, ...c);
      const vertex = (x, y, z, normal, surface) => [x, y, z, ...normal, (x + 1) / 2, y / (2 * ratio) + 0.5, surface];
      const point = (x, y) => [-1 + 2 * x / size, ratio - 2 * ratio * y / size];
      const filled = (x, y) => x >= 0 && y >= 0 && x < size && y < size && occupied[y * size + x];
      const smoothNormal = (cx, cy, fallback) => {
        let gx = 0, gy = 0;
        for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {
          if (filled(Math.floor(cx + dx), Math.floor(cy + dy))) { gx += dx; gy += dy; }
        }
        const nx = -gx, ny = gy / ratio, length = Math.hypot(nx, ny);
        return length > 0 ? [nx / length, ny / length, 0] : fallback;
      };
      const face = (left, right, row) => {
        const [x0, top] = point(left, row), [x1, bottom] = point(right, row + 1);
        const positions = [[x0,bottom], [x1,bottom], [x1,top], [x0,top]];
        const front = positions.map(([x,y]) => vertex(x,y,halfDepth,[0,0,1],0));
        const back = positions.map(([x,y]) => vertex(x,y,-halfDepth,[0,0,-1],1));
        triangle(front[0],front[1],front[2]); triangle(front[0],front[2],front[3]);
        triangle(back[0],back[2],back[1]); triangle(back[0],back[3],back[2]);
      };
      const wall = (a, b, normal) => {
        const p = (xy,z) => vertex(xy[0],xy[1],z,normal,2);
        triangle(p(a,-halfDepth),p(b,-halfDepth),p(b,halfDepth));
        triangle(p(a,-halfDepth),p(b,halfDepth),p(a,halfDepth));
      };
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size;) {
          if (!filled(x,y)) { x++; continue; }
          const left = x; while (x < size && filled(x,y)) x++; face(left,x,y);
        }
        for (let x = 0; x < size; x++) {
          if (!filled(x,y)) continue;
          if (!filled(x,y+1)) wall(point(x,y+1),point(x+1,y+1),smoothNormal(x+.5,y+1,[0,-1,0]));
          if (!filled(x+1,y)) wall(point(x+1,y+1),point(x+1,y),smoothNormal(x+1,y+.5,[1,0,0]));
          if (!filled(x,y-1)) wall(point(x+1,y),point(x,y),smoothNormal(x+.5,y,[0,1,0]));
          if (!filled(x-1,y)) wall(point(x,y),point(x,y+1),smoothNormal(x,y+.5,[-1,0,0]));
        }
      }
      return new Float32Array(vertices);
    }

    draw() {
      if (!this.gl || !this.spec || this.hidden || this.dataset.ready !== 'true') return;
      const gl = this.gl, rectangle = this.canvas.getBoundingClientRect();
      if (!rectangle.width || !rectangle.height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2), width = Math.round(rectangle.width * dpr), height = Math.round(rectangle.height * dpr);
      if (this.canvas.width !== width || this.canvas.height !== height) { this.canvas.width = width; this.canvas.height = height; }
      const appearance = materials[this.spec.material];
      gl.viewport(0, 0, width, height);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.useProgram(this.program);
      gl.uniform2f(this.uniform.uAngles, this.yaw, this.pitch);
      const aspect = width / height;
      const fit = Math.max(1, this.spec.heightCm / this.spec.widthCm, 1 / aspect);
      gl.uniform1f(this.uniform.uDistance, 3 * fit / this.zoom);
      gl.uniform1f(this.uniform.uAspect, aspect);
      gl.uniform3fv(this.uniform.uFront, appearance.front);
      gl.uniform3fv(this.uniform.uBack, appearance.back);
      gl.uniform3fv(this.uniform.uEdge, appearance.edge);
      gl.uniform1f(this.uniform.uShine, appearance.shine);
      gl.uniform3fv(this.uniform.uOpacity, [this.spec.opticalAppearance.frontOpacity, this.spec.opticalAppearance.backOpacity, this.spec.opticalAppearance.edgeOpacity]);
      gl.enable(gl.BLEND);
      gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      for (const culledFace of [gl.FRONT, gl.BACK]) {
        gl.clear(gl.DEPTH_BUFFER_BIT);
        gl.cullFace(culledFace);
        gl.depthMask(true);
        gl.colorMask(false, false, false, false);
        gl.depthFunc(gl.LESS);
        gl.drawArrays(gl.TRIANGLES, 0, this.count);
        gl.depthMask(false);
        gl.colorMask(true, true, true, true);
        gl.depthFunc(gl.LEQUAL);
        gl.enable(gl.POLYGON_OFFSET_FILL);
        gl.polygonOffset(-2, -2);
        gl.drawArrays(gl.TRIANGLES, 0, this.count);
        gl.disable(gl.POLYGON_OFFSET_FILL);
      }
      gl.depthMask(true);
      gl.depthFunc(gl.LESS);
      gl.cullFace(gl.BACK);
      gl.disable(gl.BLEND);
      this.dataset.yaw = String(this.yaw);
      this.dataset.pitch = String(this.pitch);
      this.dataset.zoom = String(this.zoom);
      this.dispatchEvent(new CustomEvent('ornament-model-render', { bubbles: true, detail: { referenceOnly: true } }));
    }
  }
  if (!customElements.get('venro-ornament-transparent-3d')) customElements.define('venro-ornament-transparent-3d', VenroOrnamentTransparent3D);
})();
